import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type ProjectsListFilterKind = 'text' | 'select' | 'none';

type SortDirection = 'asc' | 'desc';

export interface ProjectsListColumn<T> {
  /** Field key — used for sorting, filtering and column order. */
  field: string;
  /** Column header label (already translated). */
  label: string;
  /** Default true. When false the header is not clickable and no sort applies. */
  sortable?: boolean;
  /** How the per-column filter input renders. Default 'text'. */
  filter?: ProjectsListFilterKind;
  /** Options for a 'select' filter. */
  selectOptions?: { value: string; label: string }[];
  /** "All" label for a 'select' filter's empty option. */
  selectAllLabel?: string;
  /** Custom sort accessor (e.g. an amount derived from another dataset). */
  sortValue?: (row: T) => number | string | null | undefined;
  /** Direction when the column is first chosen. Default 'asc'; a date the
   *  user wants the newest of first (e.g. "Senast uppdaterad") uses 'desc'. */
  defaultSortDirection?: SortDirection;
  /** Second key for rows that are equal on this column, always ascending —
   *  e.g. project number under each company for admin. */
  thenBy?: (row: T) => number;
  /** Custom filter accessor; defaults to the row's `field` value stringified. */
  filterValue?: (row: T) => string;
  /** Cell renderer. */
  render: (row: T) => React.ReactNode;
}

export interface ProjectsListSort {
  field: string;
  direction: SortDirection;
}

export interface ProjectsListMobileFilter {
  /** Field the mobile free-text search binds to. */
  searchField: string;
  /** Optional status-style select shown next to the search. */
  select?: { field: string; allLabel: string; options: { value: string; label: string }[] };
  /** Optional sort select: on mobile there are no column headers to click. */
  sort?: { label: string; options: (ProjectsListSort & { label: string })[] };
}

/** What a server-mode list asks for whenever its page, sort or filters change. */
export interface ProjectsListServerQuery {
  page: number;
  pageSize: number;
  sort: ProjectsListSort;
  filters: Record<string, string>;
}

/**
 * Server mode (ClickUp 869fjkyz1): the server searches, filters, sorts and
 * pages; `rows` is the current page and `total` counts every match.
 */
export interface ProjectsListServerMode {
  total: number;
  loading?: boolean;
  onQueryChange: (query: ProjectsListServerQuery) => void;
}

export interface ProjectsListTableProps<T> {
  rows: T[];
  columns: ProjectsListColumn<T>[];
  rowKey: (row: T) => string;
  /** Navigate to the row (double-click on desktop / arrow action / card action).
   *  Without it rows have nothing to open: no action column and no double-click. */
  onOpen?: (row: T) => void;
  /** Rows that cannot be opened get no open button; all rows can by default. */
  canOpen?: (row: T) => boolean;
  /** Single-click row selection (optional). */
  onSelect?: (row: T) => void;
  selectedKey?: string | null;
  /** Title/label of the per-row "go to" action. */
  actionTitle: string;
  emptyText: string;
  noResultsText: string;
  /** Page-owned toolbar (create/export/go-to buttons). */
  toolbar?: React.ReactNode;
  /** Controlled column order (array of column fields). */
  columnOrder: string[];
  /** When provided, columns become drag-reorderable and this persists the order. */
  onColumnReorder?: (order: string[]) => void;
  reorderTitle?: string;
  /** Initial sort field (defaults to the first column). */
  initialSortField?: string;
  /** Initial sort direction (defaults to the field's own default). */
  initialSortDirection?: SortDirection;
  /** Controlled sort, e.g. a saved choice; the table sorts itself without it. */
  sort?: ProjectsListSort;
  onSortChange?: (sort: ProjectsListSort) => void;
  /** Initial per-column filter values (e.g. seeded from a URL query param). */
  initialFilters?: Record<string, string>;
  /** Mobile search + optional status filter. */
  mobileFilter?: ProjectsListMobileFilter;
  /** Page-owned mobile card renderer. */
  renderMobileCard: (row: T, helpers: { open: () => void; selected: boolean }) => React.ReactNode;
  /** Notified whenever the visible (filtered + sorted, all pages) rows change —
   *  e.g. so the page can export exactly what matches the active filters. */
  onVisibleRowsChange?: (rows: T[]) => void;
  /** Rows per page. Defaults to 10. */
  pageSize?: number;
  /** "Showing 1–10 of 25 …" below the list; defaults to the projects wording. */
  rangeText?: (range: { from: number; to: number; total: number }) => string;
  /** Test ids for the desktop table and the mobile card list. */
  testIds?: { table?: string; cards?: string };
  /** Let the server filter, sort and page instead of the table itself. */
  server?: ProjectsListServerMode;
}


const DEFAULT_PAGE_SIZE = 10;
/** How long typing in a filter waits before a server-mode list asks again. */
const SERVER_FILTER_DELAY_MS = 300;

/**
 * The current page number as a field: type a page and press Enter (or leave
 * the field) to go there. Focusing selects the number so typing replaces it.
 * Only digits can be typed; an empty, zero or too large value — or Escape —
 * puts the current page back.
 */
function PageNumberInput({ currentPage, totalPages, onChange, label }: {
  currentPage: number;
  totalPages: number;
  onChange: (page: number) => void;
  label: string;
}) {
  const [draft, setDraft] = useState(String(currentPage));
  useEffect(() => setDraft(String(currentPage)), [currentPage]);

  const commit = () => {
    const page = Number(draft);
    const valid = draft !== '' && page >= 1 && page <= totalPages;
    if (valid) onChange(page);
    setDraft(String(valid ? page : currentPage));
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      className="projects-list-page-input"
      aria-label={label}
      value={draft}
      size={Math.max(2, String(totalPages).length)}
      maxLength={String(totalPages).length}
      disabled={totalPages <= 1}
      onChange={e => setDraft(e.target.value.replace(/\D/g, ''))}
      // Select the number, so typing replaces it instead of adding to it.
      onFocus={e => e.target.select()}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          setDraft(String(currentPage));
        }
      }}
    />
  );
}

/**
 * Shared, presentational projects list: a sortable, per-column-filterable and
 * (optionally) drag-reorderable table on desktop plus a card view on mobile.
 * It owns sort/filter/pagination/selection UI state only; the caller owns the
 * data, the column definitions (including how each cell renders), navigation
 * and any page chrome (create/export). The component decides nothing about
 * roles or permissions.
 */
export function ProjectsListTable<T>({
  rows,
  columns,
  rowKey,
  onOpen,
  canOpen,
  onSelect,
  selectedKey,
  actionTitle,
  emptyText,
  noResultsText,
  toolbar,
  columnOrder,
  onColumnReorder,
  reorderTitle,
  initialSortField,
  initialSortDirection,
  sort,
  onSortChange,
  initialFilters,
  mobileFilter,
  renderMobileCard,
  onVisibleRowsChange,
  pageSize = DEFAULT_PAGE_SIZE,
  rangeText,
  testIds,
  server,
}: ProjectsListTableProps<T>) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<Record<string, string>>(initialFilters ?? {});
  const [ownSort, setOwnSort] = useState<ProjectsListSort>(() => {
    const field = initialSortField ?? columns[0]?.field ?? '';
    const direction = initialSortDirection
      ?? columns.find(column => column.field === field)?.defaultSortDirection ?? 'asc';
    return { field, direction };
  });
  const { field: sortField, direction: sortDirection } = sort ?? ownSort;
  const changeSort = (next: ProjectsListSort) => {
    setOwnSort(next);
    onSortChange?.(next);
  };
  const [page, setPage] = useState(1);

  // Server mode asks again only once typing has paused.
  const [settledFilters, setSettledFilters] = useState(filters);
  useEffect(() => {
    if (!server) return undefined;
    const timer = setTimeout(() => setSettledFilters(filters), SERVER_FILTER_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // A new search or sort starts on page 1; the query goes out once per change.
  const lastServerQuery = useRef<string | null>(null);
  useEffect(() => {
    if (!server) return;
    const key = JSON.stringify([settledFilters, sortField, sortDirection]);
    if (lastServerQuery.current !== null && lastServerQuery.current !== key && page !== 1) {
      lastServerQuery.current = key;
      setPage(1);
      return;
    }
    lastServerQuery.current = key;
    server.onQueryChange({ page, pageSize, sort: { field: sortField, direction: sortDirection }, filters: settledFilters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, settledFilters, sortField, sortDirection]);

  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const columnByField = useMemo(() => {
    const map: Record<string, ProjectsListColumn<T>> = {};
    columns.forEach(c => { map[c.field] = c; });
    return map;
  }, [columns]);

  const orderedColumns = useMemo(
    () => columnOrder.map(field => columnByField[field]).filter(Boolean) as ProjectsListColumn<T>[],
    [columnOrder, columnByField]
  );

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSort = (field: string) => {
    if (columnByField[field]?.sortable === false) return;
    if (sortField === field) {
      changeSort({ field, direction: sortDirection === 'asc' ? 'desc' : 'asc' });
    } else {
      changeSort({ field, direction: columnByField[field]?.defaultSortDirection ?? 'asc' });
    }
  };

  const handleSortKeyDown = (e: React.KeyboardEvent<HTMLTableCellElement>, field: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSort(field);
    }
  };

  const rawValue = (row: T, col: ProjectsListColumn<T>) => {
    if (col.filterValue) return col.filterValue(row);
    return String((row as Record<string, unknown>)[col.field] ?? '');
  };

  const filteredAndSorted = useMemo(() => {
    if (server) return rows;
    let result = [...rows];

    Object.entries(filters).forEach(([field, filterValue]) => {
      if (!filterValue) return;
      const col = columnByField[field];
      result = result.filter(row => {
        const value = (col ? rawValue(row, col) : String((row as Record<string, unknown>)[field] ?? '')).toLowerCase();
        // Exact match for select filters, partial for free text.
        if (col?.filter === 'select') return value === filterValue.toLowerCase();
        return value.includes(filterValue.toLowerCase());
      });
    });

    const col = columnByField[sortField];
    if (col) {
      result.sort((a, b) => {
        let aVal: unknown = col.sortValue ? col.sortValue(a) : (a as Record<string, unknown>)[sortField];
        let bVal: unknown = col.sortValue ? col.sortValue(b) : (b as Record<string, unknown>)[sortField];

        let order = 0;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          order = sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        } else {
          const aStr = String(aVal ?? '').toLowerCase();
          const bStr = String(bVal ?? '').toLowerCase();
          if (aStr < bStr) order = sortDirection === 'asc' ? -1 : 1;
          if (aStr > bStr) order = sortDirection === 'asc' ? 1 : -1;
        }
        return order || (col.thenBy ? col.thenBy(a) - col.thenBy(b) : 0);
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filters, sortField, sortDirection, columnByField, !!server]);

  useEffect(() => {
    onVisibleRowsChange?.(filteredAndSorted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredAndSorted]);

  // The filtered set changed shape (new filter/sort/data) — start over on page 1.
  useEffect(() => {
    if (!server) setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortField, sortDirection, server ? null : rows]);

  const totalRows = server ? server.total : filteredAndSorted.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const currentPage = Math.min(page, totalPages);
  // Fewer matches than before (e.g. a row was removed): go to the last page.
  useEffect(() => {
    if (server && !server.loading && page > totalPages) setPage(totalPages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server?.total, server?.loading]);
  const pageStart = (currentPage - 1) * pageSize;
  const paginatedRows = server ? rows : filteredAndSorted.slice(pageStart, pageStart + pageSize);
  const rangeFrom = totalRows === 0 ? 0 : pageStart + 1;
  const rangeTo = server ? Math.min(pageStart + rows.length, totalRows) : Math.min(pageStart + pageSize, totalRows);

  const handleDragStart = (index: number) => { dragItem.current = index; };
  const handleDragEnter = (index: number) => { dragOverItem.current = index; };
  const handleDragEnd = () => {
    if (!onColumnReorder || dragItem.current === null || dragOverItem.current === null || dragItem.current === dragOverItem.current) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }
    const newOrder = [...columnOrder];
    const dragged = newOrder.splice(dragItem.current, 1)[0];
    newOrder.splice(dragOverItem.current, 0, dragged);
    onColumnReorder(newOrder);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const renderSortIcon = (field: string) =>
    sortField === field ? <i className={`feather-chevron-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i> : null;

  const ariaSortFor = (col: ProjectsListColumn<T>): React.AriaAttributes['aria-sort'] => {
    if (col.sortable === false) return undefined;
    if (sortField !== col.field) return 'none';
    return sortDirection === 'asc' ? 'ascending' : 'descending';
  };

  const renderFilterInput = (col: ProjectsListColumn<T>) => {
    if (col.filter === 'none') return null;
    const filterLabel = t('projects.filterColumn', { column: col.label, defaultValue: 'Filtrera {{column}}' });
    if (col.filter === 'select') {
      return (
        <select
          className="form-select form-select-sm"
          aria-label={filterLabel}
          value={filters[col.field] || ''}
          onChange={e => handleFilterChange(col.field, e.target.value)}
        >
          <option value="">{col.selectAllLabel ?? ''}</option>
          {(col.selectOptions ?? []).map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      );
    }
    return (
      <input
        type="text"
        className="form-control form-control-sm"
        placeholder={t('projects.search', 'Sök')}
        aria-label={filterLabel}
        value={filters[col.field] || ''}
        onChange={e => handleFilterChange(col.field, e.target.value)}
      />
    );
  };

  const noneToShow = server ? rows.length === 0 && !server.loading : filteredAndSorted.length === 0;
  const message = server
    ? (Object.values(settledFilters).some(value => value.trim()) ? noResultsText : emptyText)
    : rows.length === 0 ? emptyText : noResultsText;
  const canReorder = !!onColumnReorder;

  const renderPagination = () => (
    <nav
      className="projects-list-pagination d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3"
      aria-label={t('projects.pagination.navLabel', 'Sidnavigering')}
    >
      <span className="text-muted small">
        {rangeText
          ? rangeText({ from: rangeFrom, to: rangeTo, total: totalRows })
          : t('projects.showingRange', { from: rangeFrom, to: rangeTo, total: totalRows, defaultValue: 'Visar {{from}}–{{to}} av {{total}} projekt' })}
      </span>
      <ul className="pagination mb-0">
        <li className={`page-item ${currentPage <= 1 ? 'disabled' : ''}`}>
          <button
            type="button"
            className="page-link"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
            aria-label={t('projects.pagination.previous', 'Föregående')}
          >
            <i className="feather-chevron-left" aria-hidden="true"></i>
          </button>
        </li>
        <li className="page-item">
          {/* A label, so a tap anywhere in the 44px box puts the cursor in the field. */}
          <label className="page-link projects-list-page-jump mb-0">
            <PageNumberInput
              currentPage={currentPage}
              totalPages={totalPages}
              onChange={setPage}
              label={t('projects.pagination.goToPage', 'Gå till sida')}
            />
            <span>/ {totalPages}</span>
          </label>
        </li>
        <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
          <button
            type="button"
            className="page-link"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            aria-label={t('projects.pagination.next', 'Nästa')}
          >
            <i className="feather-chevron-right" aria-hidden="true"></i>
          </button>
        </li>
      </ul>
    </nav>
  );

  return (
    <>
      {toolbar && <div className="d-flex flex-wrap gap-2 mb-4">{toolbar}</div>}

      {/* Desktop: table */}
      <div className="d-none d-md-block">
        <div className="table-responsive">
          <table className="table table-hover table-striped table-bordered projects-list-table" data-testid={testIds?.table} aria-busy={server?.loading || undefined}>
            <thead>
              <tr>
                {orderedColumns.map((col, index) => (
                  <th
                    key={col.field}
                    draggable={canReorder}
                    onDragStart={canReorder ? () => handleDragStart(index) : undefined}
                    onDragEnter={canReorder ? () => handleDragEnter(index) : undefined}
                    onDragEnd={canReorder ? handleDragEnd : undefined}
                    onDragOver={canReorder ? (e) => e.preventDefault() : undefined}
                    onClick={() => handleSort(col.field)}
                    onKeyDown={col.sortable === false ? undefined : (e) => handleSortKeyDown(e, col.field)}
                    tabIndex={col.sortable === false ? undefined : 0}
                    aria-sort={ariaSortFor(col)}
                    style={{ cursor: canReorder ? 'grab' : 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
                    title={canReorder ? reorderTitle : undefined}
                  >
                    {canReorder && <i className="feather-move me-1" style={{ opacity: 0.4, fontSize: '12px' }}></i>}
                    {col.label}
                    {renderSortIcon(col.field)}
                  </th>
                ))}
                {onOpen && <th>{t('projects.columns.actions', 'Åtgärder')}</th>}
              </tr>
              <tr>
                {orderedColumns.map(col => (
                  <th key={`filter-${col.field}`} className="p-1">
                    {renderFilterInput(col)}
                  </th>
                ))}
                {onOpen && <th></th>}
              </tr>
            </thead>
            <tbody>
              {noneToShow ? (
                <tr>
                  <td colSpan={orderedColumns.length + (onOpen ? 1 : 0)} className="text-center text-muted py-4">{message}</td>
                </tr>
              ) : (
                paginatedRows.map((row) => {
                  const key = rowKey(row);
                  const open = onOpen && (!canOpen || canOpen(row)) ? onOpen : undefined;
                  return (
                    <tr
                      key={key}
                      onClick={() => onSelect?.(row)}
                      onDoubleClick={open ? () => open(row) : undefined}
                      className={selectedKey === key ? 'table-primary' : ''}
                      style={{ cursor: open || onSelect ? 'pointer' : undefined }}
                    >
                      {orderedColumns.map(col => (
                        <td key={col.field}>{col.render(row)}</td>
                      ))}
                      {onOpen && (
                        <td>
                          {open && <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm btn-light-primary"
                              onClick={(e) => { e.stopPropagation(); open(row); }}
                              title={actionTitle}
                            >
                              <i className="feather-arrow-right"></i>
                            </button>
                          </div>}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile: card view */}
      <div className="d-md-none">
        {mobileFilter && (
          <div className="mb-3 projects-list-mobile-filter">
            <div className="row g-2">
              <div className={mobileFilter.select && !mobileFilter.sort ? 'col-8' : 'col-12'}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t('projects.search', 'Sök')}
                  aria-label={t('projects.search', 'Sök')}
                  value={filters[mobileFilter.searchField] || ''}
                  onChange={(e) => handleFilterChange(mobileFilter.searchField, e.target.value)}
                />
              </div>
              {mobileFilter.select && (
                <div className={mobileFilter.sort ? 'col-12 col-sm-6' : 'col-4'}>
                  <select
                    className="form-select form-select-sm"
                    aria-label={mobileFilter.select.allLabel}
                    value={filters[mobileFilter.select.field] || ''}
                    onChange={e => handleFilterChange(mobileFilter.select!.field, e.target.value)}
                  >
                    <option value="">{mobileFilter.select.allLabel}</option>
                    {mobileFilter.select.options.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              )}
              {mobileFilter.sort && (() => {
                const options = mobileFilter.sort.options;
                const current = `${sortField}:${sortDirection}`;
                const known = options.some(option => `${option.field}:${option.direction}` === current);
                return (
                  <div className={mobileFilter.select ? 'col-12 col-sm-6' : 'col-12'}>
                    <select
                      className="form-select form-select-sm"
                      aria-label={mobileFilter.sort.label}
                      value={current}
                      onChange={e => {
                        const [field, direction] = e.target.value.split(':');
                        if (field && (direction === 'asc' || direction === 'desc')) changeSort({ field, direction });
                      }}
                    >
                      {/* A column sort chosen on a wider screen has no option of its own here. */}
                      {!known && <option value={current}>{mobileFilter.sort.label}</option>}
                      {options.map(option => (
                        <option key={`${option.field}:${option.direction}`} value={`${option.field}:${option.direction}`}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
        {noneToShow ? (
          <p className="text-muted text-center py-3">{message}</p>
        ) : (
          <div data-testid={testIds?.cards} aria-busy={server?.loading || undefined}>
            {paginatedRows.map(row => (
              <React.Fragment key={rowKey(row)}>
                {renderMobileCard(row, { open: () => onOpen?.(row), selected: selectedKey === rowKey(row) })}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {!noneToShow && (!server || totalRows > 0) && renderPagination()}
    </>
  );
}
